import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  CheckCircle2,
  ExternalLink,
  KeyRound,
  Pause,
  Percent,
  Play,
  RefreshCw,
  Siren,
  TriangleAlert,
  UserRoundCog,
  ArrowUpCircle,
} from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { EmptyState, Page, PageHeader } from '@/components/shell'
import {
  ContractWalletBar,
  sameAddress,
  useContractWallet,
} from '@/features/contracts/wallet-bar'
import {
  api,
  ApiError,
  type ManagerControlResult,
  type ManagerControlStatus,
  type ManagerPauseState,
} from '@/lib/api'
import {
  contractWalletErrorMessage,
  sendManagerControlOperation,
  waitForContractOperation,
  type ManagerControlWrite,
  inspectManagerUpgrade,
} from '@/lib/contract-wallet'
import {
  auditActionLabel,
  explorerAddressUrl,
  explorerTxUrl,
  formatPauseUntil,
  shortAddress,
} from '@/lib/format'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/dashboard/contracts/manager')({
  component: ContractManagerPage,
})

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
const MAX_POOL_FEE = 999_999

function isEvmAddress(value: string): boolean {
  return ADDRESS_RE.test(value.trim())
}

/** V4 静态费率单位是百万分之一；后台用百分比展示，最多精确到 0.0001%。 */
function formatPoolFeePercent(poolFee: number): string {
  const whole = Math.floor(poolFee / 10_000)
  const fraction = String(poolFee % 10_000)
    .padStart(4, '0')
    .replace(/0+$/, '')
  return fraction ? `${whole}.${fraction}%` : `${whole}%`
}

function parsePoolFeePercent(value: string): number | null {
  const match = /^(0|[1-9]\d?)(?:\.(\d{1,4}))?$/.exec(value.trim())
  if (!match) return null
  const poolFee = Number(match[1]) * 10_000 + Number((match[2] ?? '').padEnd(4, '0') || '0')
  return poolFee <= MAX_POOL_FEE ? poolFee : null
}

/**
 * 紧急开关的中文说明。
 *
 * `userFunds` 由服务端给出，标记这一档暂停会不会把**用户自己的钱**关在里面
 * （卖出、分红领取）。这两档要和「暂停平台提手续费」明确区分开——
 * 后者只影响平台收入，前者影响的是别人的资产。
 */
const PAUSE_COPY: Record<string, { title: string; effect: string }> = {
  launch: { title: '发行新代币', effect: '暂停后无法通过 Manager 创建新代币。' },
  buy: { title: '曲线买入', effect: '暂停后用户无法在联合曲线上买入。' },
  curveFee: { title: '曲线手续费提取', effect: '暂停后平台无法提取曲线手续费，不影响用户。' },
  lpFee: { title: 'LP 手续费提取', effect: '暂停后平台无法提取 LP 手续费，不影响用户。' },
  sell: { title: '曲线卖出', effect: '暂停后用户无法卖出——这是把用户资金的出口关掉。' },
  dividend: { title: '分红领取', effect: '暂停后持有人无法领取已分配的分红。' },
}

function pauseTitle(pause: ManagerPauseState): string {
  return PAUSE_COPY[pause.key]?.title ?? pause.key
}

function pauseEffect(pause: ManagerPauseState): string {
  return PAUSE_COPY[pause.key]?.effect ?? '这一档开关的具体影响未在后台登记，请查阅合约文档。'
}

const DURATION_OPTIONS = [
  { hours: 1, label: '1 小时' },
  { hours: 6, label: '6 小时' },
  { hours: 24, label: '24 小时' },
  { hours: 72, label: '72 小时' },
] as const

// ── 待补写审计的交易 ────────────────────────────────────────────────────
//
// 与名单管理同一套口径：交易一旦发出去就必须先补上审计，绝不能在这之前
// 让人再发一笔。区别只在这里的操作是全局开关和所有权，重复发送的代价更大。

type ManagerAuditPayload = ManagerControlWrite & { reason: string; txHash: string }

const PENDING_STORAGE_KEY = 'contract-manager-audit-pending'

function readStoredPending(): ManagerAuditPayload | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(PENDING_STORAGE_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as ManagerAuditPayload
    const validKind =
      value.kind === 'poolFee' ||
      value.kind === 'pause' ||
      value.kind === 'signer' ||
      value.kind === 'ownerTransfer' ||
      value.kind === 'ownerAccept' ||
      value.kind === 'upgrade'
    if (
      !validKind ||
      typeof value.reason !== 'string' ||
      !/^0x[0-9a-fA-F]{64}$/.test(value.txHash) ||
      (value.kind === 'poolFee' &&
        (!Number.isInteger(value.newPoolFee) ||
          value.newPoolFee < 0 ||
          value.newPoolFee > MAX_POOL_FEE)) ||
      (value.kind === 'pause' && !/^\d+$/.test(value.until)) ||
      ((value.kind === 'signer' || value.kind === 'ownerTransfer') &&
        !isEvmAddress(value.targetAddress)) ||
      (value.kind === 'upgrade' && !isEvmAddress(value.implementation))
    ) {
      throw new Error('invalid pending operation')
    }
    return value
  } catch {
    try {
      window.sessionStorage.removeItem(PENDING_STORAGE_KEY)
    } catch {
      // 存储被策略禁用时只能靠内存锁，至少本标签页不会重复发送。
    }
    return null
  }
}

function storePending(pending: ManagerAuditPayload | null): void {
  try {
    if (pending) window.sessionStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(pending))
    else window.sessionStorage.removeItem(PENDING_STORAGE_KEY)
  } catch {
    // 存不下也不能解锁：哈希已经在手上了。
  }
}

class SubmittedTransactionError extends Error {
  constructor(
    readonly pending: ManagerAuditPayload,
    cause: unknown,
  ) {
    super(contractWalletErrorMessage(cause))
  }
}

class AuditPendingError extends Error {
  readonly terminal: boolean

  constructor(
    readonly pending: ManagerAuditPayload,
    cause: unknown,
  ) {
    super(cause instanceof ApiError ? cause.message : '审计接口暂时不可用。')
    // transaction_failed = 链上确实回滚了，这笔交易不可能再补写成功，解除锁定。
    this.terminal = cause instanceof ApiError && cause.errorCode === 'transaction_failed'
  }
}

// ── 待确认的操作 ────────────────────────────────────────────────────────

type OperationSpec =
  | { kind: 'poolFee'; current: number }
  | { kind: 'pause'; pauseKind: number; label: string; effect: string; resume: boolean }
  | { kind: 'signer'; current: string | null }
  | { kind: 'ownerTransfer'; current: string }
  | { kind: 'ownerAccept'; from: string }
  | { kind: 'upgrade'; implementation: string; current: string }

/**
 * 把状态收窄到「链上真的读到了」那一支。
 *
 * ⚠️ 必须经过这个带显式入参类型的函数。直接写
 * `status.data && status.data.ready ? status.data : null` 收不窄——
 * `status` 本身是 UseQueryResult 的联合，`.data` 上的判别式narrowing 到不了，
 * 结果 `owner` 仍是 `0x… | null`，而 `manager` 会塌成 `string | null`。
 */
type ReadyStatus = Extract<ManagerControlStatus, { ready: true }>

function readyStatus(value: ManagerControlStatus | undefined): ReadyStatus | null {
  return value && value.ready ? value : null
}

const OPERATION_TITLE: Record<OperationSpec['kind'], string> = {
  poolFee: '修改 V4 LP 默认费率',
  pause: '紧急开关',
  signer: '更换签名人',
  ownerTransfer: '移交合约所有权',
  ownerAccept: '接受合约所有权',
  upgrade: '切换 Manager 实现',
}

function ContractManagerPage() {
  const can = useCan()
  const qc = useQueryClient()
  const canManage = can('contract', 'manage')

  const status = useQuery({
    queryKey: ['contract-manager-status'],
    queryFn: () => api.get<ManagerControlStatus>('/contracts/manager/status'),
    staleTime: 15_000,
    retry: false,
  })
  const { wallet, walletBusy, connect, switchChain, disconnect } = useContractWallet()

  const [operation, setOperation] = useState<OperationSpec | null>(null)
  const [pending, setPending] = useState<ManagerAuditPayload | null>(() => readStoredPending())
  const [lastResult, setLastResult] = useState<ManagerControlResult | null>(null)

  const ready = readyStatus(status.data)
  const onChain = ready !== null && wallet.chainId === ready.chainId
  const isOwner = onChain && sameAddress(wallet.address, ready?.owner)
  const isPendingOwner = onChain && sameAddress(wallet.address, ready?.pendingOwner)

  const mutation = useMutation({
    mutationFn: async (input: ManagerControlWrite & { reason: string }) => {
      // 有待补写的交易时，这个按钮只做补写，绝不再碰钱包。
      if (pending) {
        try {
          return await api.post<ManagerControlResult>('/contracts/manager/operations', pending)
        } catch (error) {
          throw new AuditPendingError(pending, error)
        }
      }

      if (!ready || !wallet.address) throw new Error('请先连接可签名的钱包。')

      const { reason, ...write } = input
      const txHash = await sendManagerControlOperation({
        ...write,
        account: wallet.address,
        manager: ready.manager,
      })
      const next: ManagerAuditPayload = { ...write, reason, txHash }
      setPending(next)
      storePending(next)

      let receipt: Awaited<ReturnType<typeof waitForContractOperation>>
      try {
        receipt = await waitForContractOperation(txHash)
      } catch (error) {
        throw new SubmittedTransactionError(next, error)
      }
      if (receipt.status !== 'success') {
        setPending(null)
        storePending(null)
        throw new Error('链上交易已回滚。')
      }

      try {
        return await api.post<ManagerControlResult>('/contracts/manager/operations', next)
      } catch (error) {
        throw new AuditPendingError(next, error)
      }
    },
    onSuccess: (result) => {
      setLastResult(result)
      setPending(null)
      storePending(null)
      setOperation(null)
      void qc.invalidateQueries({ queryKey: ['contract-manager-status'] })
      toast.success('操作已确认并写入审计', {
        description: `交易 ${shortAddress(result.txHash)} · 区块 ${result.blockNumber}`,
      })
    },
    onError: (error) => {
      if (error instanceof SubmittedTransactionError) {
        setPending(error.pending)
        setOperation(null)
        toast.warning('交易已提交，等待链上确认', {
          description: '请勿重复提交。可用原交易重新核验链上状态并补写审计。',
        })
        return
      }

      if (error instanceof AuditPendingError) {
        if (error.terminal) {
          setPending(null)
          storePending(null)
          setOperation(null)
          toast.error('链上交易已回滚', {
            description: '原交易未生效，已解除锁定；核对参数后可以重新提交。',
          })
          return
        }
        setPending(error.pending)
        setOperation(null)
        toast.warning('交易尚未完成审计', {
          description: `${error.message} 可用补写入口重试审计，不会再次发送交易。`,
        })
        return
      }

      toast.error('操作失败', { description: contractWalletErrorMessage(error) })
    },
  })

  return (
    <Page>
      <PageHeader
        title="合约控制"
        description="Ignix Manager 的 V4 LP 默认费率、所有权、签名人与紧急开关。状态直接读链上，写操作全部由浏览器钱包签名——后台不接触私钥，只在交易上链后核验并写入审计。"
        actions={
          <Button
            variant="outline"
            size="sm"
            className="pressable rounded-apple-sm"
            disabled={status.isFetching}
            onClick={() => void status.refetch()}
          >
            <RefreshCw className={cn('size-3.5', status.isFetching && 'animate-spin')} />
            刷新状态
          </Button>
        }
      />

      {canManage && (
        <ContractWalletBar
          status={status.data}
          isLoading={status.isLoading}
          isError={status.isError}
          error={status.error}
          onRetry={() => void status.refetch()}
          wallet={wallet}
          walletBusy={walletBusy}
          expected={[
            { label: 'owner', address: ready?.owner ?? null },
            { label: 'pending owner', address: ready?.pendingOwner ?? null },
          ]}
          onConnect={connect}
          onDisconnect={disconnect}
          onSwitchChain={switchChain}
        />
      )}

      {pending && (
        <PendingAuditBar
          pending={pending}
          busy={mutation.isPending}
          onRetry={() => mutation.mutate({ ...pending })}
        />
      )}

      {lastResult && !pending && (
        <a
          href={explorerTxUrl(lastResult.txHash)}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 rounded-apple bg-green/[0.07] px-3.5 py-2.5 text-[11.5px] text-green hover:bg-green/[0.1]"
        >
          <CheckCircle2 className="size-3.5 shrink-0" />
          <span className="min-w-0 flex-1 truncate">
            最近成功：{auditActionLabel(lastResult.action)} · {shortAddress(lastResult.txHash)}
          </span>
          <ExternalLink className="size-3 shrink-0" />
        </a>
      )}

      <StatusSection query={status} />

      <PoolFeeSection
        status={ready}
        canManage={canManage}
        canOperate={canManage && isOwner}
        onOperate={(current) => setOperation({ kind: 'poolFee', current })}
        blockedReason={
          !canManage
            ? '只有超级管理员可以修改费率。'
            : !ready
              ? '链上状态不可读时不提供修改入口。'
              : !isOwner
                ? `只有 Manager owner（${shortAddress(ready.owner)}）能修改费率。`
                : null
        }
      />

      <PauseSection
        query={status}
        canOperate={canManage && isOwner}
        onOperate={(pause) =>
          setOperation({
            kind: 'pause',
            pauseKind: pause.kind,
            label: pauseTitle(pause),
            effect: pauseEffect(pause),
            resume: pause.active,
          })
        }
        blockedReason={
          !canManage
            ? '需要「管理合约名单」权限才能操作紧急开关。'
            : !ready
              ? '链上状态不可读时不提供操作入口。'
              : !isOwner
                ? `只有 Manager owner（${shortAddress(ready.owner)}）能操作紧急开关。`
                : null
        }
      />

      {canManage && (
        <OwnershipSection
          status={ready}
          isOwner={isOwner}
          isPendingOwner={isPendingOwner}
          onOperate={setOperation}
        />
      )}

      <OperationDialog
        operation={operation}
        busy={mutation.isPending}
        locked={Boolean(pending)}
        onOpenChange={(open) => {
          if (!open && !mutation.isPending) setOperation(null)
        }}
        onSubmit={(input) => mutation.mutate(input)}
      />
    </Page>
  )
}

function PendingAuditBar({
  pending,
  busy,
  onRetry,
}: {
  pending: ManagerAuditPayload
  busy: boolean
  onRetry: () => void
}) {
  return (
    <div
      role="alert"
      className="surface flex flex-wrap items-center gap-3 rounded-apple bg-warn/[0.06] px-4 py-3"
    >
      <TriangleAlert className="size-4 shrink-0 text-warn" />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-txt">有一笔交易尚未写入审计</p>
        <p className="mt-0.5 text-[11.5px] leading-relaxed text-mut2">
          {OPERATION_TITLE[pending.kind]} · 交易{' '}
          <a
            href={explorerTxUrl(pending.txHash)}
            target="_blank"
            rel="noreferrer"
            className="font-mono underline-offset-2 hover:text-cyan hover:underline"
          >
            {shortAddress(pending.txHash)}
          </a>
          。在补写成功之前，本页不会再发起任何新的链上交易。
        </p>
      </div>
      <Button
        size="sm"
        className="pressable shrink-0 rounded-apple-sm"
        disabled={busy}
        onClick={onRetry}
      >
        {busy ? '核验并补写中…' : '补写审计记录'}
      </Button>
    </div>
  )
}

function StatusSection({ query }: { query: ReturnType<typeof useQuery<ManagerControlStatus>> }) {
  if (query.isLoading) {
    return (
      <section className="surface space-y-3 p-6">
        <Skeleton className="h-4 w-24 rounded-apple-sm" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-12 rounded-apple" />
          ))}
        </div>
      </section>
    )
  }

  if (query.isError || !query.data) {
    return (
      <section className="surface p-0">
        <EmptyState
          icon={TriangleAlert}
          title="读不到 Manager 状态"
          description={
            query.error instanceof ApiError ? query.error.message : '请检查后台 API 与网络连接。'
          }
        />
        <div className="flex justify-center pb-6">
          <Button
            variant="outline"
            size="sm"
            className="pressable rounded-apple-sm"
            onClick={() => void query.refetch()}
          >
            <RefreshCw className="size-3.5" />
            重试
          </Button>
        </div>
      </section>
    )
  }

  const status = query.data

  if (!status.ready) {
    return (
      <section className="surface space-y-2 p-6">
        <div className="section-label">链上状态</div>
        <div className="flex items-start gap-2 rounded-apple bg-warn/[0.07] px-3.5 py-3 text-[12px] leading-relaxed text-warn">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">无法读取 Manager 状态</p>
            <p className="mt-1 text-mut">{status.reason ?? '链上校验不可用。'}</p>
            <p className="mt-1 text-mut2">
              合约地址：
              <span className="font-mono">{status.manager ?? '未配置'}</span> · 期望链{' '}
              <span className="tnum font-mono">{status.chainId}</span>
            </p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="surface space-y-4 p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="section-label">链上状态</div>
        <span className="tnum font-mono text-[11px] text-mut2">X Layer · {status.chainId}</span>
      </div>

      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <AddressFact label="Manager 合约" value={status.manager} />
        <AddressFact label="owner" value={status.owner} />
        <AddressFact
          label="pending owner"
          value={status.pendingOwner}
          empty="无待接受的移交"
          tone={status.pendingOwner ? 'warn' : 'default'}
        />
        <AddressFact label="signer（签名人）" value={status.signer} />
        <BoolFact label="configured" value={status.configured} />
        <BoolFact label="configured2" value={status.configured2} />
      </dl>

      {status.pendingOwner && (
        <p className="flex items-start gap-2 rounded-apple bg-warn/[0.07] px-3.5 py-2.5 text-[11.5px] leading-relaxed text-warn">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          所有权移交处于两步中的第一步：只有{' '}
          <span className="font-mono">{shortAddress(status.pendingOwner)}</span>{' '}
          用它自己的钱包执行接受，owner 才会真正变更。在那之前当前 owner 仍然有效。
        </p>
      )}
    </section>
  )
}

function AddressFact({
  label,
  value,
  empty = '—',
  tone = 'default',
}: {
  label: string
  value: string | null
  empty?: string
  tone?: 'default' | 'warn'
}) {
  const zero = value !== null && sameAddress(value, ZERO_ADDRESS)

  return (
    <div className="surface-inset min-w-0 px-3.5 py-2.5">
      <dt className="text-[11px] text-mut2">{label}</dt>
      <dd className="mt-1 min-w-0">
        {value ? (
          <a
            href={explorerAddressUrl(value)}
            target="_blank"
            rel="noreferrer"
            title={value}
            className={cn(
              'inline-flex min-w-0 items-center gap-1 font-mono text-[12px] transition-colors hover:text-cyan',
              tone === 'warn' ? 'text-warn' : 'text-txt',
            )}
          >
            <span className="truncate">{shortAddress(value)}</span>
            {zero && <span className="shrink-0 text-[10.5px] text-mut2">（零地址）</span>}
            <ExternalLink className="size-3 shrink-0 opacity-60" />
          </a>
        ) : (
          <span className="text-[12px] text-mut2">{empty}</span>
        )}
      </dd>
    </div>
  )
}

function BoolFact({ label, value }: { label: string; value: boolean | null }) {
  return (
    <div className="surface-inset min-w-0 px-3.5 py-2.5">
      <dt className="text-[11px] text-mut2">{label}</dt>
      <dd
        className={cn(
          'mt-1 text-[12px] font-medium',
          value === null ? 'text-mut2' : value ? 'text-green' : 'text-warn',
        )}
      >
        {value === null ? '—' : value ? '已配置' : '未配置'}
      </dd>
    </div>
  )
}

function PoolFeeSection({
  status,
  canManage,
  canOperate,
  blockedReason,
  onOperate,
}: {
  status: ReadyStatus | null
  canManage: boolean
  canOperate: boolean
  blockedReason: string | null
  onOperate: (current: number) => void
}) {
  return (
    <section className="surface p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
            <Percent className="size-4 text-cyan" />
          </div>
          <div className="min-w-0">
            <h2 className="text-[15px] font-medium tracking-tight text-txt">V4 LP 默认费率</h2>
            <p className="mt-1 max-w-3xl text-[12px] leading-relaxed text-mut2">
              仅用于之后新创建代币的 Uniswap V4 池；已有代币保留创建时的费率，V2 仍使用 Router02
              固定的 0.3%。
            </p>
          </div>
        </div>

        {status ? (
          <div className="flex shrink-0 items-center gap-3">
            <div className="text-right">
              <div className="stat-num text-[24px] leading-none text-txt">
                {formatPoolFeePercent(status.poolFee)}
              </div>
              <div className="mt-1 font-mono text-[10.5px] text-mut2">
                合约值 {status.poolFee.toLocaleString('en-US')} / 1,000,000
              </div>
            </div>
            {canManage && (
              <Button
                variant="outline"
                size="sm"
                className="pressable h-9 rounded-apple text-[12.5px]"
                disabled={!canOperate}
                onClick={() => onOperate(status.poolFee)}
              >
                修改费率
              </Button>
            )}
          </div>
        ) : (
          <span className="text-[12px] text-mut2">链上状态不可读</span>
        )}
      </div>

      {blockedReason && <p className="mt-3 text-[11.5px] text-mut2">{blockedReason}</p>}
    </section>
  )
}

function PauseSection({
  query,
  canOperate,
  blockedReason,
  onOperate,
}: {
  query: ReturnType<typeof useQuery<ManagerControlStatus>>
  canOperate: boolean
  blockedReason: string | null
  onOperate: (pause: ManagerPauseState) => void
}) {
  if (query.isLoading) {
    return <Skeleton className="h-52 w-full rounded-apple" />
  }

  const status = query.data
  const pauses = status?.ready ? status.pauses : []

  return (
    <section className="surface p-6">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="flex items-center gap-2.5">
          <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
            <Siren className="size-4 text-cyan" />
          </div>
          <div>
            <h2 className="text-[15px] font-medium tracking-tight text-txt">紧急开关</h2>
            <p className="mt-0.5 text-[12px] text-mut2">
              每一档独立，按 Unix 时间戳到期自动解除；「恢复」是把截止时间写回 0。
            </p>
          </div>
        </div>
      </div>

      {pauses.length === 0 ? (
        <div className="mt-4">
          <EmptyState
            icon={Siren}
            title="暂无可读的开关状态"
            description="链上状态不可读时这里不会渲染任何开关，避免把「读不到」误显示成「未暂停」。"
          />
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-line/50">
          {pauses.map((pause) => (
            <li key={pause.kind} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-medium text-txt">{pauseTitle(pause)}</span>
                  <span
                    className={cn(
                      'rounded-full px-1.5 py-px font-mono text-[10px]',
                      pause.active ? 'bg-rose/15 text-rose' : 'bg-edge/10 text-mut2',
                    )}
                  >
                    {pause.active ? 'PAUSED' : 'ACTIVE'}
                  </span>
                  {pause.userFunds && (
                    <span className="rounded-full bg-warn/15 px-1.5 py-px text-[10px] text-warn">
                      影响用户资金
                    </span>
                  )}
                  <span className="font-mono text-[10.5px] text-dim">kind={pause.kind}</span>
                </div>
                <p className="mt-0.5 text-[11.5px] leading-relaxed text-mut2">
                  {pauseEffect(pause)}
                  {pause.active && (
                    <>
                      {' '}
                      当前暂停至 <span className="tnum">{formatPauseUntil(pause.until)}</span>。
                    </>
                  )}
                </p>
              </div>

              {canOperate ? (
                <Button
                  variant={pause.active ? 'outline' : 'ghost'}
                  size="sm"
                  className={cn(
                    'pressable h-8 shrink-0 rounded-apple-sm text-[12.5px]',
                    !pause.active && 'text-rose hover:bg-rose/10 hover:text-rose',
                  )}
                  onClick={() => onOperate(pause)}
                >
                  {pause.active ? (
                    <>
                      <Play className="size-3.5" />
                      恢复
                    </>
                  ) : (
                    <>
                      <Pause className="size-3.5" />
                      暂停
                    </>
                  )}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {blockedReason && pauses.length > 0 && (
        <p className="mt-3 text-[11.5px] leading-relaxed text-mut2">{blockedReason}</p>
      )}
    </section>
  )
}

function OwnershipSection({
  status,
  isOwner,
  isPendingOwner,
  onOperate,
}: {
  status: ReadyStatus | null
  isOwner: boolean
  isPendingOwner: boolean
  onOperate: (operation: OperationSpec) => void
}) {
  return (
    <div className="grid gap-3.5 lg:grid-cols-2">
      <section className="surface flex flex-col p-6">
        <div className="flex items-start gap-3">
          <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
            <UserRoundCog className="size-4 text-cyan" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-[15px] font-medium tracking-tight text-txt">所有权</h2>
            <p className="mt-1 text-[12px] leading-relaxed text-mut2">
              两步移交：当前 owner 先发起 transferOwnership，新地址再用自己的钱包
              acceptOwnership。中途可以由 owner 重新指向别的地址来覆盖。
            </p>
          </div>
        </div>

        <div className="mt-5 flex-1 space-y-2 text-[11.5px]">
          <FactRow label="当前 owner" value={status?.owner ?? null} />
          <FactRow label="待接受" value={status?.pendingOwner ?? null} empty="无" />
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            className="pressable h-9 rounded-apple text-[12.5px]"
            disabled={!isOwner || !status}
            onClick={() => status && onOperate({ kind: 'ownerTransfer', current: status.owner })}
          >
            发起移交
          </Button>
          <Button
            size="sm"
            className="pressable h-9 rounded-apple text-[12.5px]"
            disabled={!isPendingOwner || !status?.pendingOwner}
            onClick={() =>
              status?.pendingOwner && onOperate({ kind: 'ownerAccept', from: status.owner })
            }
          >
            接受所有权
          </Button>
        </div>

        <p className="mt-3 text-[11px] leading-relaxed text-mut2">
          {!status
            ? '链上状态不可读，两个操作都不可用。'
            : isPendingOwner
              ? '当前钱包是待接受方，可以执行接受。'
              : isOwner
                ? status.pendingOwner
                  ? '已有一笔待接受的移交；再次发起会覆盖它。接受必须由对方钱包执行。'
                  : '当前钱包是 owner，可以发起移交。'
                : '当前钱包既不是 owner 也不是待接受方，两个操作都不可用。'}
        </p>

        {/*
          放弃所有权（renounce）在这里刻意没有入口：服务端的 managerOperationBody
          只接受 pause / signer / ownerTransfer / ownerAccept 四种，不会为放弃所有权
          核验交易、也不会写审计。一个做完查不到、又永远回不去的按钮不该存在。
        */}
        <p className="mt-2 text-[11px] leading-relaxed text-dim">
          放弃所有权不在后台提供：服务端不核验这类交易，也无法写入审计。
        </p>
      </section>

      <ManagerUpgradeSection
        manager={status?.manager ?? null}
        currentImplementation={null}
        isOwner={isOwner}
        onOperate={onOperate}
      />

      <section className="surface flex flex-col p-6">
        <div className="flex items-start gap-3">
          <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
            <KeyRound className="size-4 text-cyan" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-[15px] font-medium tracking-tight text-txt">签名人</h2>
            <p className="mt-1 text-[12px] leading-relaxed text-mut2">
              signer 是后端签发链上凭证用的地址。换掉它，旧地址签发的、尚未上链的凭证会立即失效。
            </p>
          </div>
        </div>

        <div className="mt-5 flex-1 space-y-2 text-[11.5px]">
          <FactRow label="当前 signer" value={status?.signer ?? null} />
        </div>

        <Button
          variant="outline"
          size="sm"
          className="pressable mt-5 h-9 rounded-apple text-[12.5px]"
          disabled={!isOwner || !status}
          onClick={() => status && onOperate({ kind: 'signer', current: status.signer })}
        >
          更换签名人
        </Button>

        <p className="mt-3 text-[11px] leading-relaxed text-mut2">
          {!status
            ? '链上状态不可读，无法更换。'
            : isOwner
              ? '换人之前先确认新地址的私钥已经接入签发服务，否则会立刻中断发行。'
              : `只有 owner（${shortAddress(status.owner)}）能更换签名人。`}
        </p>
      </section>
    </div>
  )
}

function FactRow({
  label,
  value,
  empty = '—',
}: {
  label: string
  value: string | null
  empty?: string
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 text-mut2">{label}</span>
      {value ? (
        <a
          href={explorerAddressUrl(value)}
          target="_blank"
          rel="noreferrer"
          title={value}
          className="min-w-0 truncate font-mono text-txt transition-colors hover:text-cyan"
        >
          {shortAddress(value)}
        </a>
      ) : (
        <span className="text-mut2">{empty}</span>
      )}
    </div>
  )
}

/**
 * 危险操作的统一确认对话框。
 *
 * 理由与参数都收在这里而不是散在卡片上，是为了让「点按钮 → 立刻发交易」这条路
 * **不存在**：每一次链上写入都必须先经过一次带完整摘要的确认。
 */
/**
 * Manager 实现升级的阶段 B（ignix `docs/25` V-10）。
 *
 * 阶段 A 由热部署钥在 CI/本地跑 `ColdWalletManagerUpgrade.s.sol:DeployManagerImplementation`，
 * 它部署并 link 新实现、打印地址与 wiring hash；本节只做**签名前的核对**再交给钱包。
 *
 * ⚠️ 这一节的价值全在那几条断言上。少了它，冷钱包用户面对的就是一个地址和一串
 * 十六进制——尤其是「三个库有没有链接进去」，漏了不会在任何环节报错，只有带税的币
 * 在毕业那一刻炸。
 */
function ManagerUpgradeSection({
  manager,
  isOwner,
  onOperate,
}: {
  manager: string | null
  currentImplementation: string | null
  isOwner: boolean
  onOperate: (spec: OperationSpec) => void
}) {
  const [implementation, setImplementation] = useState('')
  const [libraries, setLibraries] = useState('')
  const [report, setReport] = useState<Awaited<ReturnType<typeof inspectManagerUpgrade>> | null>(
    null,
  )
  const [error, setError] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)

  const libs = libraries.split(/[\s,]+/).filter(Boolean)
  const inputsValid = isEvmAddress(implementation.trim()) && libs.length === 3 && libs.every(isEvmAddress)
  const passed =
    report !== null && report.hasCode && report.differs && report.missingLibraries.length === 0

  // 任何一个输入变了，上一次的核对结论立刻作废——否则会出现「核对的是 A、签的是 B」。
  useEffect(() => {
    setReport(null)
    setError(null)
  }, [implementation, libraries])

  const runCheck = async () => {
    if (!manager || !inputsValid) return
    setChecking(true)
    setError(null)
    try {
      setReport(await inspectManagerUpgrade({ manager, implementation: implementation.trim(), libraries: libs }))
    } catch (cause) {
      setReport(null)
      setError(contractWalletErrorMessage(cause))
    } finally {
      setChecking(false)
    }
  }

  return (
    <section className="surface flex flex-col p-6">
      <div className="flex items-start gap-3">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <ArrowUpCircle className="size-4 text-cyan" />
        </div>
        <div className="min-w-0">
          <div className="section-label">实现升级（阶段 B）</div>
          <p className="mt-1 text-[12px] leading-relaxed text-mut2">
            先由部署钥跑阶段 A（<code className="text-[11px]">DeployManagerImplementation</code>），
            把它打印的新实现与三个库地址填到这里。本节只核对，不部署。
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        <div>
          <Label className="text-[11.5px] text-mut">新实现地址</Label>
          <Input
            value={implementation}
            onChange={(e) => setImplementation(e.target.value)}
            placeholder="0x…"
            spellCheck={false}
            className="mt-1 h-9 font-mono text-[12px]"
          />
        </div>
        <div>
          <Label className="text-[11.5px] text-mut">
            三个外部库（CurveTrading / LaunchLogic / V2Graduation，逗号或空格分隔）
          </Label>
          <Input
            value={libraries}
            onChange={(e) => setLibraries(e.target.value)}
            placeholder="0x…, 0x…, 0x…"
            spellCheck={false}
            className="mt-1 h-9 font-mono text-[12px]"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          className="pressable h-9 rounded-apple text-[12.5px]"
          disabled={!manager || !inputsValid || checking}
          onClick={() => void runCheck()}
        >
          {checking ? '核对中…' : '① 链上核对'}
        </Button>
        <Button
          size="sm"
          className="pressable h-9 rounded-apple text-[12.5px]"
          disabled={!passed || !isOwner || !report}
          onClick={() =>
            report &&
            onOperate({
              kind: 'upgrade',
              implementation: implementation.trim(),
              current: report.currentImplementation,
            })
          }
        >
          ② 签名升级
        </Button>
      </div>

      {error ? <p className="mt-3 text-[11.5px] text-rose">{error}</p> : null}

      {report ? (
        <div className="mt-4 flex-1 space-y-2 text-[11.5px]">
          <FactRow label="链上当前实现" value={report.currentImplementation} />
          <CheckRow
            ok={report.hasCode}
            label="新实现有代码"
            detail={report.hasCode ? `${report.codeSize} 字节` : '目标地址没有代码'}
          />
          <CheckRow
            ok={report.differs}
            label="与当前实现不同"
            detail={report.differs ? '' : '这是一次空升级'}
          />
          <CheckRow
            ok={report.missingLibraries.length === 0}
            label="三个库已链接进新实现"
            detail={
              report.missingLibraries.length === 0
                ? '字节码里三个地址都在'
                : `缺 ${report.missingLibraries.join(', ')}`
            }
          />
        </div>
      ) : null}

      <p className="mt-4 text-[11px] leading-relaxed text-mut2">
        {!isOwner
          ? '当前钱包不是 owner，签名按钮不可用。'
          : passed
            ? '四条核对全过。上链后必须再跑阶段 C 校验 19 项接线——实现槽对了不代表接线没变。'
            : '先完成链上核对，四条全过才会解锁签名。'}
      </p>
    </section>
  )
}

function CheckRow({ ok, label, detail }: { ok: boolean; label: string; detail?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-mut2">{label}</span>
      <span className={ok ? 'text-up' : 'text-rose'}>
        {ok ? '通过' : '未通过'}
        {detail ? <span className="ml-2 text-[11px] text-dim">{detail}</span> : null}
      </span>
    </div>
  )
}

function OperationDialog({
  operation,
  busy,
  locked,
  onOpenChange,
  onSubmit,
}: {
  operation: OperationSpec | null
  busy: boolean
  locked: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (input: ManagerControlWrite & { reason: string }) => void
}) {
  const [reason, setReason] = useState('')
  const [target, setTarget] = useState('')
  const [feePercent, setFeePercent] = useState('')
  const [hours, setHours] = useState<number>(6)

  const trimmedReason = reason.trim()
  const reasonValid = trimmedReason.length >= 10 && trimmedReason.length <= 200
  const needsTarget = operation?.kind === 'signer' || operation?.kind === 'ownerTransfer'
  const targetValid = !needsTarget || isEvmAddress(target)
  const zeroTarget = needsTarget && sameAddress(target.trim(), ZERO_ADDRESS)
  const parsedPoolFee = operation?.kind === 'poolFee' ? parsePoolFeePercent(feePercent) : null
  const poolFeeValid = operation?.kind !== 'poolFee' || parsedPoolFee !== null
  const poolFeeChanged = operation?.kind !== 'poolFee' || parsedPoolFee !== operation.current
  const canSubmit =
    Boolean(operation) &&
    reasonValid &&
    targetValid &&
    !zeroTarget &&
    poolFeeValid &&
    poolFeeChanged &&
    !locked

  /**
   * 对话框组件本身常驻，只有内容随 `operation` 挂载/卸载。不在关闭时清空的话，
   * 上一次的理由和地址会原样留到下一个操作里——那意味着「暂停卖出」的理由
   * 会被带到「移交所有权」上，而审计流水只记结果，事后完全看不出来。
   */
  useEffect(() => {
    if (operation?.kind === 'poolFee') {
      setFeePercent(formatPoolFeePercent(operation.current).replace('%', ''))
      return
    }
    if (!operation) {
      setReason('')
      setTarget('')
      setFeePercent('')
      setHours(6)
    }
  }, [operation])

  const submit = () => {
    if (!operation || !canSubmit) return
    if (operation.kind === 'poolFee') {
      if (parsedPoolFee === null) return
      onSubmit({ kind: 'poolFee', newPoolFee: parsedPoolFee, reason: trimmedReason })
      return
    }
    if (operation.kind === 'pause') {
      const until = operation.resume
        ? '0'
        : String(Math.floor(Date.now() / 1000) + hours * 3600)
      onSubmit({ kind: 'pause', pauseKind: operation.pauseKind, until, reason: trimmedReason })
      return
    }
    if (operation.kind === 'ownerAccept') {
      onSubmit({ kind: 'ownerAccept', reason: trimmedReason })
      return
    }
    if (operation.kind === 'upgrade') {
      onSubmit({ kind: 'upgrade', implementation: operation.implementation, reason: trimmedReason })
      return
    }
    onSubmit({ kind: operation.kind, targetAddress: target.trim(), reason: trimmedReason })
  }

  const title = !operation
    ? ''
    : operation.kind === 'pause'
      ? operation.resume
        ? `确认恢复「${operation.label}」？`
        : `确认暂停「${operation.label}」？`
      : `确认${OPERATION_TITLE[operation.kind]}？`

  const danger: ReactNode = !operation
    ? null
    : operation.kind === 'pause'
      ? operation.resume
        ? `恢复后 ${operation.label} 立即重新开放。请确认引发暂停的问题已经处理完毕。`
        : operation.effect
      : operation.kind === 'poolFee'
        ? '修改只影响之后新创建代币的 V4 池。已有代币的费率快照不会改变，V2 也不会改变。'
        : operation.kind === 'ownerTransfer'
          ? '地址填错不会立刻出事——对方接受之前 owner 不变，你可以再发一次覆盖。但一旦对方接受，控制权就完全转移，无法收回。'
          : operation.kind === 'ownerAccept'
            ? '接受之后你成为唯一 owner，原 owner 立即失去全部权限。'
            : operation.kind === 'upgrade'
              ? '实现切换立即生效，全站所有代币在下一笔交易上就走新逻辑。上链后必须跑阶段 C 校验接线（ignix docs/25 V-10）——实现槽对了不代表 19 项接线没变。'
              : '旧 signer 已签发但尚未上链的凭证会立即失效。换人前请确认新私钥已接入签发服务。'

  return (
    <AlertDialog
      open={Boolean(operation)}
      onOpenChange={(open) => {
        if (busy) return
        onOpenChange(open)
      }}
    >
      <AlertDialogContent className="rounded-apple-xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[17px] text-txt">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-[12.5px] leading-relaxed text-mut">
            确认后浏览器钱包会弹出交易签名。交易上链后，后台会重新解码这笔交易的调用参数，
            对不上就不会写审计。
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3.5">
          {operation?.kind === 'poolFee' && (
            <div className="space-y-1.5">
              <Label htmlFor="manager-pool-fee" className="text-[12.5px]">
                新的 V4 LP 默认费率（%）
              </Label>
              <div className="relative">
                <Input
                  id="manager-pool-fee"
                  inputMode="decimal"
                  value={feePercent}
                  placeholder="例如 0.3"
                  aria-invalid={feePercent.length > 0 && !poolFeeValid}
                  className="h-9 rounded-apple pr-8 font-mono text-[12px]"
                  onChange={(event) => setFeePercent(event.target.value)}
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[12px] text-mut2">
                  %
                </span>
              </div>
              {!poolFeeValid && feePercent.length > 0 && (
                <p className="text-[11px] text-rose">请输入 0–99.9999，最多 4 位小数。</p>
              )}
              {poolFeeValid && !poolFeeChanged && (
                <p className="text-[11px] text-warn">新费率与当前值相同，无需发送交易。</p>
              )}
              {parsedPoolFee !== null && (
                <p className="text-[11px] leading-relaxed text-mut2">
                  将写入合约值{' '}
                  <span className="font-mono text-txt">
                    {parsedPoolFee.toLocaleString('en-US')}
                  </span>{' '}
                  / 1,000,000。0% 也是合法配置。
                </p>
              )}
            </div>
          )}

          {operation?.kind === 'pause' && !operation.resume && (
            <div className="space-y-1.5">
              <Label className="text-[12.5px]">暂停时长</Label>
              <div role="radiogroup" aria-label="暂停时长" className="flex flex-wrap gap-1.5">
                {DURATION_OPTIONS.map((option) => (
                  <button
                    key={option.hours}
                    type="button"
                    role="radio"
                    aria-checked={hours === option.hours}
                    className={cn(
                      'pressable h-8 rounded-apple-sm px-3 text-[12px] transition-colors',
                      hours === option.hours
                        ? 'bg-cyan/12 font-medium text-cyan inset-ring inset-ring-cyan/20'
                        : 'bg-edge/[0.06] text-mut hover:text-txt',
                    )}
                    onClick={() => setHours(option.hours)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] leading-relaxed text-mut2">
                到期自动解除，不需要人工恢复；期间可以随时手动恢复。
              </p>
            </div>
          )}

          {needsTarget && (
            <div className="space-y-1.5">
              <Label htmlFor="manager-target" className="text-[12.5px]">
                {operation?.kind === 'signer' ? '新签名人地址' : '新 owner 地址'}
              </Label>
              <Input
                id="manager-target"
                value={target}
                placeholder="0x…"
                spellCheck={false}
                autoComplete="off"
                aria-invalid={target.length > 0 && (!targetValid || zeroTarget)}
                className="h-9 rounded-apple font-mono text-[12px]"
                onChange={(event) => setTarget(event.target.value)}
              />
              {target.length > 0 && !targetValid && (
                <p className="text-[11px] text-rose">地址必须是 0x 开头的 40 位十六进制字符。</p>
              )}
              {zeroTarget && (
                <p className="text-[11px] text-rose">不能填零地址——服务端会直接拒绝。</p>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="manager-reason" className="text-[12.5px]">
                操作理由
              </Label>
              <span className="text-[10.5px] text-mut2">{reason.length}/200</span>
            </div>
            <Textarea
              id="manager-reason"
              value={reason}
              maxLength={200}
              placeholder="说明触发原因、工单编号或决策依据（至少 10 个字符）"
              className="min-h-20 resize-none rounded-apple text-[12px]"
              aria-invalid={reason.length > 0 && !reasonValid}
              onChange={(event) => setReason(event.target.value)}
            />
            {reason.length > 0 && !reasonValid && (
              <p className="text-[11px] text-rose">操作理由至少需要 10 个字符。</p>
            )}
          </div>
        </div>

        <p className="flex items-start gap-2 rounded-apple bg-rose/[0.06] px-3 py-2.5 text-[11.5px] leading-relaxed text-rose">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          {danger}
        </p>

        {locked && (
          <p className="text-[11.5px] leading-relaxed text-warn">
            上一笔交易还没写入审计，本页暂时不会发起新的链上交易。
          </p>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-apple" disabled={busy}>
            返回检查
          </AlertDialogCancel>
          <Button className="pressable rounded-apple" disabled={!canSubmit || busy} onClick={submit}>
            {busy ? '等待钱包或链上确认…' : '在钱包中确认'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
